import { useState, useEffect, useMemo, useCallback } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  Mail,
  GraduationCap,
  UserCircle,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  fetchLexiconDirectoryUsers,
  updateLexiconDirectoryUserActive,
  deleteLexiconDirectoryUser,
  isManageableLexiconUser,
  type LexiconDirectoryUser,
  type LexiconTableRole,
} from "@/lib/lexiconUsersApi";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

type RoleFilter = "all" | LexiconTableRole;

type AdminUsersProps = {
  canManageUsers: boolean;
  listRefreshKey?: number;
  onAddUser: () => void;
  onEditUser: (user: LexiconDirectoryUser) => void;
  onDirectoryLoaded?: (users: LexiconDirectoryUser[]) => void;
  /** A just-saved row to merge in place (optimistic) instead of a full reload. */
  upsertUser?: LexiconDirectoryUser | null;
  /** Called once `upsertUser` has been applied so the parent can clear it. */
  onUpsertApplied?: () => void;
};

/**
 * User directory: staff JWT + tenant (`GET …/jain_lexicon/users/`).
 */
export function AdminUsers({
  canManageUsers,
  listRefreshKey = 0,
  onAddUser,
  onEditUser,
  onDirectoryLoaded,
  upsertUser,
  onUpsertApplied,
}: AdminUsersProps) {
  const [users, setUsers] = useState<LexiconDirectoryUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [deleteTarget, setDeleteTarget] = useState<LexiconDirectoryUser | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const { users: list, error } = await fetchLexiconDirectoryUsers();
      if (error) {
        setFetchError(error);
        setUsers([]);
        toastApiError(error);
      } else {
        setUsers(list);
        onDirectoryLoaded?.(list);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load users";
      setFetchError(msg);
      setUsers([]);
      toastApiError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [onDirectoryLoaded]);

  useEffect(() => {
    void fetchUsers();
  }, [fetchUsers, listRefreshKey]);

  // Apply an optimistic create/edit in place instead of reloading the list.
  useEffect(() => {
    if (!upsertUser) {
      return;
    }
    const key = `${upsertUser.id}\0${upsertUser.user_id}`;
    setUsers(list => {
      const exists = list.some(u => `${u.id}\0${u.user_id}` === key);
      return exists
        ? list.map(u => (`${u.id}\0${u.user_id}` === key ? upsertUser : u))
        : [upsertUser, ...list];
    });
    onUpsertApplied?.();
  }, [upsertUser, onUpsertApplied]);

  const openEdit = (profile: LexiconDirectoryUser) => {
    if (!canManageUsers || !isManageableLexiconUser(profile)) {
      return;
    }
    onEditUser(profile);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !canManageUsers) {
      return;
    }
    setIsDeleting(true);
    const { ok, error } = await deleteLexiconDirectoryUser(deleteTarget);
    setIsDeleting(false);
    if (!ok) {
      toastApiError(error ?? "Could not delete user.");
      return;
    }
    toast.success("User removed.");
    const key = `${deleteTarget.id}\0${deleteTarget.user_id}`;
    setUsers(list => list.filter(u => `${u.id}\0${u.user_id}` !== key));
    setDeleteTarget(null);
  };

  const toggleActive = async (
    profile: LexiconDirectoryUser,
    nextActive: boolean
  ) => {
    if (!canManageUsers) {
      return;
    }
    const prev = profile.is_active;
    const rowKey = `${profile.id}\0${profile.user_id}`;
    setUsers(list =>
      list.map(u =>
        `${u.id}\0${u.user_id}` === rowKey ? { ...u, is_active: nextActive } : u
      )
    );
    const { ok, error } = await updateLexiconDirectoryUserActive(
      profile,
      nextActive
    );
    if (!ok) {
      setUsers(list =>
        list.map(u =>
          `${u.id}\0${u.user_id}` === rowKey ? { ...u, is_active: prev } : u
        )
      );
      toastApiError(error ?? "Could not update user.");
      return;
    }
    toast.success(nextActive ? "User activated." : "User deactivated.");
  };

  const afterRoleFilter = useMemo(() => {
    if (roleFilter === "all") {
      return users;
    }
    return users.filter(u => u.lexicon_role === roleFilter);
  }, [users, roleFilter]);

  const filtered = useMemo(
    () =>
      afterRoleFilter.filter(
        u =>
          !searchQuery ||
          u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          u.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [afterRoleFilter, searchQuery]
  );

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEndExclusive = Math.min(pageStart + pageSize, totalFiltered);
  const paginatedUsers = filtered.slice(pageStart, pageEndExclusive);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleFilter]);

  useEffect(() => {
    setPage(p => Math.min(p, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  const adminCount = useMemo(
    () => users.filter(u => u.lexicon_role === "admin").length,
    [users]
  );
  const scholarCount = useMemo(
    () => users.filter(u => u.lexicon_role === "scholar").length,
    [users]
  );
  const contributorCount = useMemo(
    () => users.filter(u => u.lexicon_role === "contributor").length,
    [users]
  );
  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.filter(u => !u.is_active).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="mb-1 font-serif text-2xl font-bold">User Management</h2>
          <p className="text-muted-foreground text-sm">
            {canManageUsers
              ? "Admins can create, edit, and delete scholars and contributors."
              : "View-only access. Sign in as an admin to manage users."}
          </p>
        </div>
        {canManageUsers ? (
          <Button
            type="button"
            className="shrink-0 gap-2"
            onClick={() => onAddUser()}
          >
            <Plus className="h-4 w-4" />
            Add user
          </Button>
        ) : null}
      </div>

      

      {fetchError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load users</AlertTitle>
          <AlertDescription>{fetchError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-foreground text-3xl font-bold">{users.length}</p>
            <p className="text-muted-foreground text-sm">Total listed</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-slate-700 dark:text-slate-300">
              {adminCount}
            </p>
            <p className="text-muted-foreground text-sm">Admins</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-violet-700">{scholarCount}</p>
            <p className="text-muted-foreground text-sm">Scholars</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-700">
              {contributorCount}
            </p>
            <p className="text-muted-foreground text-sm">Contributors</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-700">{activeCount}</p>
            <p className="text-muted-foreground text-sm">Active</p>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-700">{inactiveCount}</p>
            <p className="text-muted-foreground text-sm">Inactive</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <ToggleGroup
          type="single"
          value={roleFilter}
          onValueChange={v => {
            if (!v) {
              setRoleFilter("all");
              return;
            }
            setRoleFilter(v as RoleFilter);
          }}
          variant="outline"
          className="flex-wrap justify-start"
          aria-label="Filter by role"
        >
          <ToggleGroupItem value="all">All roles</ToggleGroupItem>
          <ToggleGroupItem value="admin">Admins</ToggleGroupItem>
          <ToggleGroupItem value="scholar">Scholars</ToggleGroupItem>
          <ToggleGroupItem value="contributor">Contributors</ToggleGroupItem>
        </ToggleGroup>
        <div className="relative w-full max-w-md lg:w-auto lg:min-w-[280px]">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-muted-foreground py-12 text-center text-sm">
              {fetchError
                ? ""
                : users.length === 0
                  ? "No users returned for this tenant."
                  : "No users match your search or role filter."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="hidden sm:table-cell">Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                  {canManageUsers ? (
                    <TableHead className="text-right">Actions</TableHead>
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map(user => {
                  const manageable =
                    canManageUsers && isManageableLexiconUser(user);
                  return (
                  <TableRow key={`${user.id}-${user.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold">
                          {(user.display_name || user.email || "?")
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                        <span className="font-medium">
                          {user.display_name || "No name"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {user.email ? (
                        <a
                          href={`mailto:${user.email}`}
                          className="text-primary flex items-center gap-1 text-sm hover:underline"
                        >
                          <Mail className="h-3.5 w-3.5" />
                          {user.email}
                        </a>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {user.lexicon_role === "admin" ? (
                        <Badge className="border-slate-300/40 bg-slate-200 text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100">
                          <ShieldCheck className="mr-1 h-3 w-3" /> Admin
                        </Badge>
                      ) : user.lexicon_role === "scholar" ? (
                        <Badge className="border-violet-300/40 bg-violet-100 text-violet-900 dark:bg-violet-950/50 dark:text-violet-200">
                          <GraduationCap className="mr-1 h-3 w-3" /> Scholar
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="border-amber-300/40 bg-amber-50 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
                        >
                          <UserCircle className="mr-1 h-3 w-3" /> Contributor
                        </Badge>
                      )}
                    </TableCell>
                    
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(user.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "2-digit",
                      })}
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge
                          variant="secondary"
                          className="bg-green-100 text-green-800"
                        >
                          Active
                        </Badge>
                      ) : (
                        <Badge
                          variant="secondary"
                          className="bg-red-100 text-red-800"
                        >
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch
                        checked={user.is_active}
                        disabled={!canManageUsers}
                        onCheckedChange={checked =>
                          void toggleActive(user, checked)
                        }
                      />
                    </TableCell>
                    {canManageUsers ? (
                      <TableCell className="text-right">
                        {manageable ? (
                          <div className="flex justify-end gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              title="Edit user"
                              onClick={() => openEdit(user)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="text-destructive hover:text-destructive h-8 w-8"
                              title="Delete user"
                              onClick={() => setDeleteTarget(user)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          )}
          {!isLoading && filtered.length > 0 ? (
            <div className="border-border flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <p className="text-muted-foreground text-sm tabular-nums">
                Showing{" "}
                <span className="text-foreground font-medium">
                  {pageStart + 1}
                </span>
                –
                <span className="text-foreground font-medium">
                  {pageEndExclusive}
                </span>
                {" of "}
                <span className="text-foreground font-medium">
                  {totalFiltered}
                </span>
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="admin-users-page-size"
                    className="text-muted-foreground shrink-0 text-sm whitespace-nowrap"
                  >
                    Per page
                  </Label>
                  <Select
                    value={String(pageSize)}
                    onValueChange={v => setPageSize(Number(v))}
                  >
                    <SelectTrigger
                      id="admin-users-page-size"
                      className="h-9 w-[4.75rem]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-muted-foreground px-1 text-sm tabular-nums">
                    Page {safePage} of {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1"
                    disabled={safePage >= totalPages}
                    onClick={() =>
                      setPage(p => Math.min(totalPages, p + 1))
                    }
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={open => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes{" "}
              <span className="text-foreground font-medium">
                {deleteTarget?.display_name || deleteTarget?.email || "this user"}
              </span>{" "}
              from the directory. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={e => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
